import React, { useState, useEffect } from 'react';
import { 
  Typography, Grid, Paper, Box, Button, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Divider, LinearProgress, Tooltip,
  InputAdornment, FormControl, InputLabel, Select, MenuItem, Alert,
  Collapse, Chip, Slider, Stack, alpha, useTheme, Accordion, AccordionSummary, AccordionDetails, Card, CardContent,
  Container
} from '@mui/material';
import { 
  Info, ShoppingCart, Edit, Save, Calculate, KeyboardArrowDown, KeyboardArrowUp,
  AttachMoney as AttachMoneyIcon, TrendingUp as TrendingUpIcon, 
  ExpandMore, Assessment as AssessmentIcon, AccountBalance as AccountBalanceIcon,
  Check as CheckIcon, CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { supabase } from '../../../supabaseClient';
import { useNavigate } from 'react-router-dom';
import { 
  convertUnits, 
  calculateRawMaterialCost, 
  formatUnitDisplay, 
  formatRawMaterialUnit,
  getUnitType,
  WEIGHT_UNITS,
  VOLUME_UNITS
} from '../../../utils/unitConversions';
import { usePermissions } from '../../../utils/permissions';

const CostsTab = ({ formulation, showSnackbar, ingredients, user, onUpdate }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { canAccessPage, canEditPage, hasPermission, isManagerOrAbove, userRole, hierarchyLevel, canApproveFormulations } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [formulationIngredients, setFormulationIngredients] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  
  const [laborPercentage, setLaborPercentage] = useState(40);
  
  // Global overhead percentage
  const [globalOverheadPercentage, setGlobalOverheadPercentage] = useState(28);
  
  // Overhead categories as % of the overhead budget (should sum to 100%)
  const [utilitiesPercentage, setUtilitiesPercentage] = useState(10);        
  const [facilityPercentage, setFacilityPercentage] = useState(25);          
  const [equipmentPercentage, setEquipmentPercentage] = useState(15);        
  const [adminLaborPercentage, setAdminLaborPercentage] = useState(18);      
  const [warehousePercentage, setWarehousePercentage] = useState(7);         
  const [qualityPercentage, setQualityPercentage] = useState(10);            
  const [softwarePercentage, setSoftwarePercentage] = useState(8);           
  const [insurancePercentage, setInsurancePercentage] = useState(7);
  
  const [costsInputted, setCostsInputted] = useState(false);
  const [showCostsForm, setShowCostsForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [displayUnit, setDisplayUnit] = useState(formulation?.unit_of_measure || 'g');
  const [costsCompleteLoading, setCostsCompleteLoading] = useState(false);
  
  const [costs, setCosts] = useState({
    ingredientCosts: [],
    totalIngredientCost: 0,
    laborCost: 0,
    overheadCost: 0,
    totalCost: 0,
    costPerUnit: 0
  });

  // Package calculator state
  const [packageSize, setPackageSize] = useState(8);
  const [packageUnit, setPackageUnit] = useState('oz');
  const [markupPercentage, setMarkupPercentage] = useState(250);
  const [packagingCost, setPackagingCost] = useState(2.50);

  const allUnits = [...WEIGHT_UNITS, ...VOLUME_UNITS];

  // Data loading effects
  useEffect(() => {
    const loadData = async () => {
      if (!formulation?.id) return;
      
      setLoading(true);
      try {
        const { data: ingredientsData, error: ingredientsError } = await supabase
          .from('formulation_ingredients_new')
          .select(`
            id,
            formulation_id,
            ingredient_id,
            percentage,
            amount,
            amount_unit,
            notes,
            function
          `)
          .eq('formulation_id', formulation.id);

        if (ingredientsError) throw ingredientsError;

        const materialIds = [...new Set(ingredientsData?.map(i => i.ingredient_id).filter(Boolean))];
        
        if (materialIds.length > 0) {
          const { data: materialsData, error: materialsError } = await supabase
            .from('raw_materials')
            .select(`
              id,
              name,
              sku,
              primary_unit_type,
              primary_unit_quantity,
              primary_unit_measure,
              unit_cost,
              density_g_ml,
              supplier:supplier_id(
                id,
                name
              )
            `)
            .in('id', materialIds);

          if (materialsError) throw materialsError;
          setRawMaterials(materialsData || []);
        }

        setFormulationIngredients(ingredientsData || []);

        const { data: settingsData } = await supabase
          .from('formulation_settings')
          .select('*')
          .eq('formulation_id', formulation.id)
          .single();

        if (settingsData) {
          setLaborPercentage(settingsData.labor_percentage || 40);
          setGlobalOverheadPercentage(settingsData.global_overhead_percentage || 28);
          setUtilitiesPercentage(settingsData.utilities_percentage || 10);
          setFacilityPercentage(settingsData.facility_percentage || 25);
          setEquipmentPercentage(settingsData.equipment_percentage || 15);
          setAdminLaborPercentage(settingsData.admin_labor_percentage || 18);
          setWarehousePercentage(settingsData.warehouse_percentage || 7);
          setQualityPercentage(settingsData.quality_percentage || 10);
          setSoftwarePercentage(settingsData.software_percentage || 8);
          setInsurancePercentage(settingsData.insurance_percentage || 7);
          setDisplayUnit(settingsData.display_unit || formulation.unit_of_measure || 'g');
          setCostsInputted(true);
        }

      } catch (error) {
        showSnackbar('Error loading cost data: ' + error.message, 'error');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [formulation?.id]);

  useEffect(() => {
    if (formulationIngredients.length > 0 && rawMaterials.length > 0) {
      calculateIngredientCosts();
    }
  }, [
    formulationIngredients,
    rawMaterials,
    laborPercentage,
    globalOverheadPercentage,
    utilitiesPercentage,
    facilityPercentage,
    equipmentPercentage,
    adminLaborPercentage,
    warehousePercentage,
    qualityPercentage,
    softwarePercentage,
    insurancePercentage,
    displayUnit
  ]);
  
  // Check permissions for costs tab specifically
  const canViewCosts = hasPermission('products.formulations.costs.view', 'read');
  const canEditCosts = hasPermission('products.formulations.costs.edit', 'write');
  const canDeleteCosts = hasPermission('products.formulations.costs.delete', 'delete');
  const canApprove = canApproveFormulations();

  // Permission check
  if (!canViewCosts) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '60vh',
        textAlign: 'center',
        px: 3
      }}>
        <Card sx={{
          p: 6,
          borderRadius: 4,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: 'white',
          boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
          maxWidth: 500,
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, transparent)`,
            zIndex: 0,
            opacity: 0.8,
            transition: 'opacity 0.3s ease'
          },
          '&:hover::before': {
            opacity: 1
          }
        }}>
          <Box sx={{ 
            p: 3, 
            borderRadius: 3, 
            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            mb: 6,
            display: 'inline-flex',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
          }}>
            <AttachMoneyIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
          </Box>
          <Typography variant="h3" sx={{ 
            mb: 3, 
            fontWeight: 500, 
            color: 'text.primary',
            fontSize: '2.5rem',
            letterSpacing: '-0.8px'
          }}>
            Access Restricted
          </Typography>
          <Typography variant="h6" sx={{ 
            color: 'text.secondary', 
            mb: 4,
            fontWeight: 500,
            fontSize: '1.2rem'
          }}>
            Formulation Costs Required
          </Typography>
          <Typography variant="body1" sx={{ 
            color: 'text.secondary',
            lineHeight: 1.7,
            mb: 6,
            fontSize: '1.05rem',
            fontWeight: 500
          }}>
            You don't currently have the necessary permissions to access the Formulation Costs. Contact your system administrator to request access or verify your role permissions.
          </Typography>
        </Card>
      </Box>
    );
  }
  
  // Calculate distribution total (should add up to 100% for proper distribution)
  const distributionTotal = utilitiesPercentage + facilityPercentage + equipmentPercentage + 
                           adminLaborPercentage + warehousePercentage + qualityPercentage + 
                           softwarePercentage + insurancePercentage;

  // Calculate weighted average density from formulation ingredients
  const calculateFormulationDensity = () => {
    if (!formulationIngredients.length || !rawMaterials.length) {
      return 1.0; // Default cosmetic density
    }
    
    let totalWeightedDensity = 0;
    let totalPercentage = 0;
    
    formulationIngredients.forEach(ingredient => {
      const rawMaterial = rawMaterials.find(rm => rm.id === ingredient.ingredient_id);
      const percentage = ingredient.percentage || 0;
      const materialDensity = rawMaterial?.density_g_ml;
      
      if (materialDensity && materialDensity > 0 && percentage > 0) {
        totalWeightedDensity += (materialDensity * percentage);
        totalPercentage += percentage;
      }
    });
    
    if (totalPercentage > 0) {
      const calculatedDensity = totalWeightedDensity / totalPercentage;
      return calculatedDensity;
    }
    
    return 1.0; // Default cosmetic density fallback
  };

  // Calculate package costs
  const calculatePackageCost = () => {
    if (!costs.costPerUnit || !formulation?.unit_of_measure) return 0;
    
    // Get calculated density for accurate conversions
    const formulationDensity = calculateFormulationDensity();
    
    // Convert package size to formulation units
    const packageSizeInFormulationUnits = convertUnits(
      packageSize,
      packageUnit, // Use weight ounces (oz) as-is
      formulation.unit_of_measure,
      formulationDensity // Use calculated density
    );
    
    return packageSizeInFormulationUnits * costs.costPerUnit;
  };

  const packageCostValue = calculatePackageCost();
  const totalCostBase = packageCostValue + packagingCost;
  const retailPrice = totalCostBase * (1 + markupPercentage / 100);
  
  // Calculate profit margin percentage
  const profitMarginPercentage = retailPrice > 0 ? ((retailPrice - totalCostBase) / retailPrice * 100) : 0;

  const calculateIngredientCosts = () => {
    if (!formulationIngredients.length || !rawMaterials.length) return;
    
    // Calculate weighted average density from formulation ingredients (SAME AS BATCH OVERVIEW)
    let calculatedDensity = 1.0; // Default cosmetic density
    
    if (formulationIngredients.length > 0) {
      let totalWeightedDensity = 0;
      let totalPercentage = 0;
      
      formulationIngredients.forEach(ingredient => {
        const rawMaterial = rawMaterials.find(rm => rm.id === ingredient.ingredient_id);
        const percentage = ingredient.percentage || 0;
        const materialDensity = rawMaterial?.density_g_ml;
        
        if (materialDensity && materialDensity > 0 && percentage > 0) {
          totalWeightedDensity += (materialDensity * percentage);
          totalPercentage += percentage;
        }
      });
      
      if (totalPercentage > 0) {
        calculatedDensity = totalWeightedDensity / totalPercentage;
      }
    }
    
    // First, aggregate ingredients by ingredient_id to get total usage across all phases
    const aggregatedIngredients = {};
    
    formulationIngredients.forEach(ingredient => {
      const key = ingredient.ingredient_id;
      if (!aggregatedIngredients[key]) {
        aggregatedIngredients[key] = {
          ingredient_id: ingredient.ingredient_id,
          total_percentage: 0,
          phases_used: [],
          function: ingredient.function || '', // Use function from first occurrence
          notes: ingredient.notes || '' // Use notes from first occurrence
        };
      }
      
      aggregatedIngredients[key].total_percentage += parseFloat(ingredient.percentage || 0);
      
      // Track which phases this ingredient is used in
      if (ingredient.formulation_phase_id && !aggregatedIngredients[key].phases_used.includes(ingredient.formulation_phase_id)) {
        aggregatedIngredients[key].phases_used.push(ingredient.formulation_phase_id);
      }
    });
    
    // Now calculate costs based on aggregated usage
    const ingredientCosts = Object.values(aggregatedIngredients).map(aggregatedIngredient => {
      const rawMaterial = rawMaterials.find(rm => rm.id === aggregatedIngredient.ingredient_id);
      if (!rawMaterial) {
        return {
          ingredient_id: aggregatedIngredient.ingredient_id,
          ingredient_name: 'Unknown Material',
          supplier_name: 'No supplier',
          cost_per_unit: 0,
          amount: 0,
          total_cost: 0,
          has_pricing: false,
          unit_info: 'N/A',
          total_percentage: aggregatedIngredient.total_percentage,
          phases_used: aggregatedIngredient.phases_used,
          density_display: null
        };
      }

      // Calculate total amount needed across all phases in formulation units
      const totalAmountNeeded = (aggregatedIngredient.total_percentage * formulation.target_batch_size / 100);
      
      // Calculate cost using proper raw material unit conversions with density
      const costData = calculateRawMaterialCost(
        totalAmountNeeded,
        formulation.unit_of_measure || 'g',
        rawMaterial
      );

      // Calculate raw material amount needed (same logic as IngredientsTab)
      const materialDensity = rawMaterial.density_g_ml || 1.0;
      let rawMaterialAmount = null;
      let rawMaterialUnit = null;
      let showRawMaterialConversion = false;

      // If raw material has different unit than formulation, show conversion
      if (rawMaterial.primary_unit_measure && rawMaterial.primary_unit_measure !== (formulation.unit_of_measure || 'g')) {
        try {
          rawMaterialAmount = convertUnits(
            totalAmountNeeded,
            formulation.unit_of_measure || 'g',
            rawMaterial.primary_unit_measure,
            materialDensity
          );
          rawMaterialUnit = rawMaterial.primary_unit_measure;
          showRawMaterialConversion = true;
        } catch (error) {
          console.warn(`Raw material unit conversion failed for ${rawMaterial.name}:`, error);
        }
      }

      // Calculate density-based display for backward compatibility
      let densityDisplay = null;
      
      // Only show density adjustment if it differs significantly from 1.0 AND we're not showing raw material conversion
      if (Math.abs(materialDensity - 1.0) > 0.01 && !showRawMaterialConversion) {
        const formulationUnitType = getUnitType(formulation.unit_of_measure || 'g');
        
        try {
          if (formulationUnitType === 'volume') {
            // For volume formulations, show actual weight
            const amountInML = convertUnits(totalAmountNeeded, formulation.unit_of_measure || 'g', 'ml');
            const actualWeightGrams = amountInML * materialDensity;
            const actualWeightKg = actualWeightGrams / 1000;
            
            densityDisplay = {
              amount: actualWeightKg,
              unit: 'kg',
              type: 'weight_for_volume',
              density: materialDensity
            };
          } else if (formulationUnitType === 'weight') {
            // For weight formulations, show actual volume
            const amountInGrams = convertUnits(totalAmountNeeded, formulation.unit_of_measure || 'g', 'g');
            const actualVolumeML = amountInGrams / materialDensity;
            const actualVolumeL = actualVolumeML / 1000;
            
            densityDisplay = {
              amount: actualVolumeL,
              unit: 'L',
              type: 'volume_for_weight',
              density: materialDensity
            };
          }
        } catch (error) {
          console.warn(`Density conversion failed for ${rawMaterial.name}:`, error);
        }
      }

      // Convert to display units for table display
      const amountInDisplayUnits = convertUnits(
        totalAmountNeeded,
        formulation.unit_of_measure || 'g',
        displayUnit,
        materialDensity
      );

      return {
        ingredient_id: aggregatedIngredient.ingredient_id,
        ingredient_name: rawMaterial.name,
        supplier_name: rawMaterial.supplier?.name || 'No supplier',
        cost_per_unit: costData.unitCost,
        amount: amountInDisplayUnits,
        units_needed: costData.unitsNeeded,
        total_cost: costData.totalCost,
        has_pricing: !!rawMaterial.unit_cost,
        percentage: aggregatedIngredient.total_percentage,
        unit_info: formatRawMaterialUnit(rawMaterial),
        raw_material_unit_type: rawMaterial.primary_unit_type,
        raw_material_unit_measure: rawMaterial.primary_unit_measure,
        cost_per_formulation_unit: costData.costPerFormulationUnit,
        phases_used: aggregatedIngredient.phases_used,
        density_display: densityDisplay,
        material_density: materialDensity,
        // Raw material conversion data
        raw_material_amount: rawMaterialAmount,
        raw_material_unit: rawMaterialUnit,
        show_raw_material_conversion: showRawMaterialConversion,
        formulation_amount: totalAmountNeeded
      };
    });

    const totalIngredientCost = ingredientCosts.reduce((sum, item) => sum + (item.total_cost || 0), 0);
    const laborCost = totalIngredientCost * (laborPercentage / 100);
    
    // Calculate overhead based only on raw materials cost (not compounded with labor)
    const overheadCost = totalIngredientCost * (globalOverheadPercentage / 100);
    
    const totalCost = totalIngredientCost + laborCost + overheadCost;
    
    // Use calculated formulation density for accurate batch size conversion
    const formulationDensity = calculatedDensity;
    const batchSizeInDisplayUnits = convertUnits(
      formulation.target_batch_size || 1,
      formulation.unit_of_measure || 'g',
      displayUnit,
      formulationDensity
    );
    
    const costPerUnit = batchSizeInDisplayUnits > 0 ? totalCost / batchSizeInDisplayUnits : 0;

    const newCosts = {
      ingredientCosts,
      totalIngredientCost,
      laborCost,
      overheadCost,
      totalCost,
      costPerUnit
    };

    setCosts(newCosts);

    // Auto-save costs to database if cost parameters are already set
    if (costsInputted && totalCost > 0) {
      saveCostsToDatabase({
        totalIngredientCost,
        laborCost,
        overheadCost,
        totalCost,
        costPerUnit
      });
    }
  };

  const saveCostsToDatabase = async (costsData) => {
    try {
      const { data: existingCosts, error: selectError } = await supabase
        .from('formulation_costs')
        .select('id')
        .eq('formulation_id', formulation.id)
        .order('calculated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (selectError && selectError.code !== 'PGRST116') {
        console.warn('Could not check existing costs:', selectError);
        return;
      }

      const costRecord = {
        formulation_id: formulation.id,
        batch_size: formulation.target_batch_size || 100,
        batch_unit: formulation.unit_of_measure || 'g',
        raw_material_cost: costsData.totalIngredientCost,
        labor_cost: costsData.laborCost,
        overhead_cost: costsData.overheadCost,
        total_cost: costsData.totalCost,
        total_batch_cost: costsData.totalCost,
        cost_per_unit: costsData.costPerUnit,
        currency: 'USD',
        calculated_at: new Date().toISOString()
      };

      if (existingCosts) {
        const { error: updateError } = await supabase
          .from('formulation_costs')
          .update(costRecord)
          .eq('id', existingCosts.id);
        if (updateError) {
          console.error('Failed to update costs:', updateError);
        }
      } else {
        const { error: insertError } = await supabase
          .from('formulation_costs')
          .insert([costRecord]);
        if (insertError) {
          console.error('Failed to insert costs:', insertError);
        }
      }
    } catch (error) {
      console.error('Error saving costs to database:', error);
    }
  };

  // Permission check - only Product Development Managers and Chemists can access formulations
  if (!canAccessPage('formulations')) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ mb: 2, fontWeight: 500, color: '#555' }}>
          Access Denied
        </Typography>
        <Typography variant="body1" sx={{ color: '#777' }}>
          You don't have permission to access formulation costs.
        </Typography>
      </Container>
    );
  }

  const handleSaveCosts = async () => {
    try {
      const { data: existingSettings } = await supabase
        .from('formulation_settings')
        .select('id')
        .eq('formulation_id', formulation.id)
        .single();

      const settingsData = {
        formulation_id: formulation.id,
        labor_percentage: laborPercentage,
        global_overhead_percentage: globalOverheadPercentage,
        utilities_percentage: utilitiesPercentage,
        facility_percentage: facilityPercentage,
        equipment_percentage: equipmentPercentage,
        admin_labor_percentage: adminLaborPercentage,
        warehouse_percentage: warehousePercentage,
        quality_percentage: qualityPercentage,
        software_percentage: softwarePercentage,
        insurance_percentage: insurancePercentage,
        display_unit: displayUnit
      };

      if (existingSettings) {
        await supabase
          .from('formulation_settings')
          .update(settingsData)
          .eq('id', existingSettings.id);
      } else {
        await supabase
          .from('formulation_settings')
          .insert([settingsData]);
      }

      setCostsInputted(true);
      setEditMode(false);
      
      // Save calculated costs to database after saving settings
      saveCostsToDatabase({
        totalIngredientCost: costs.totalIngredientCost,
        laborCost: costs.laborCost,
        overheadCost: costs.overheadCost,
        totalCost: costs.totalCost,
        costPerUnit: costs.costPerUnit
      });
      
      showSnackbar('Cost parameters saved successfully', 'success');
      
      // Trigger parent component to refresh
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      showSnackbar('Error saving cost parameters: ' + error.message, 'error');
    }
  };

  const formatCurrency = (amount) => `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const hasMissingPricing = costs.ingredientCosts?.some(item => !item.has_pricing);

  const handleNavigateToInventory = () => {
    if (formulation?.company_id) {
      navigate(`/company/${formulation.company_id}/inventory/raw-materials`);
    }
  };

  const handleMarkComplete = async () => {
    try {
      setCostsCompleteLoading(true);
      
      const newCompleteStatus = !formulation.costs_complete;
      
      const { error } = await supabase
        .from('formulations')
        .update({
          costs_complete: newCompleteStatus,
          costs_completed_at: newCompleteStatus ? new Date().toISOString() : null,
          costs_completed_by: newCompleteStatus ? user?.id : null
        })
        .eq('id', formulation.id);
        
      if (error) throw error;
      
      showSnackbar(
        newCompleteStatus ? 'Costs marked as complete!' : 'Costs marked as incomplete',
        'success'
      );
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      console.error('Error updating costs completion:', err);
      showSnackbar('Error: ' + err.message, 'error');
    } finally {
      setCostsCompleteLoading(false);
    }
  };

  return (
    <Box
      sx={{
        pb: 4,
        display: 'flex',
        justifyContent: 'center',
        width: '100%'
      }}
    >
      <Box sx={{ maxWidth: '100vw', width: '100%', px: { xs: 2, sm: 3 } }}>
      {/* Enhanced Header Section */}
      <Card 
        sx={{ 
          borderRadius: 3,
          mb: 3,
          border: `1px solid ${theme.palette.grey[400]}`,
          background: 'white',
          backdropFilter: 'blur(20px)',
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Box sx={{ 
                  p: 1, 
                  borderRadius: 2, 
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  backdropFilter: 'blur(10px)'
                }}>
                  <AttachMoneyIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                    Cost Analysis
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Comprehensive cost breakdown including materials, labor, and overhead expenses
                  </Typography>
                </Box>
              </Stack>
              
              {/* Quick Stats */}
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                    {costs.totalCost ? `$${costs.totalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$0.00'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Total Cost
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                    {costs.costPerUnit ? `$${costs.costPerUnit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$0.00'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Per {displayUnit}
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ 
                    fontWeight: 500, 
                    color: costs.ingredientCosts?.some(item => !item.has_pricing) ? 'warning.main' : theme.palette.primary.main
                  }}>
                    {costs.ingredientCosts?.length || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Materials
                  </Typography>
                </Box>
              </Stack>
            </Box>
            
            <Stack direction="row" spacing={2}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Display Unit</InputLabel>
                <Select
                  value={displayUnit}
                  label="Display Unit"
                  onChange={(e) => setDisplayUnit(e.target.value)}
                  sx={{ 
                    borderRadius: 3,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main
                    }
                  }}
                >
                  {allUnits.map(unit => (
                    <MenuItem key={unit} value={unit}>
                      {unit}
                      {unit === formulation?.unit_of_measure && " (formulation unit)"}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {(canEditCosts || canApprove) && (
                <Button
                  variant={formulation?.costs_complete ? "contained" : "outlined"}
                  startIcon={formulation?.costs_complete ? <CheckCircleIcon /> : <CheckIcon />}
                  onClick={handleMarkComplete}
                  disabled={costsCompleteLoading}
                  size="small"
                  sx={{
                    minWidth: 140,
                    height: 40,
                    borderRadius: 3,
                    px: 3,
                    py: 1.5,
                    background: formulation?.costs_complete 
                      ? `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`
                      : 'white',
                    border: formulation?.costs_complete 
                      ? 'none'
                      : `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                    color: formulation?.costs_complete ? 'white' : theme.palette.success.main,
                    fontWeight: 500,
                    backdropFilter: 'blur(10px)',
                    boxShadow: `0 2px 8px ${alpha(theme.palette.success.main, 0.15)}`,
                    '&:hover': {
                      background: formulation?.costs_complete 
                        ? `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`
                        : alpha(theme.palette.success.main, 0.08),
                      borderColor: formulation?.costs_complete ? 'none' : theme.palette.success.main,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 6px 20px ${alpha(theme.palette.success.main, 0.25)}`
                    },
                    '&:disabled': {
                      background: formulation?.costs_complete ? 'transparent' : theme.palette.grey[400],
                      color: formulation?.costs_complete ? 'success.main' : theme.palette.grey[100],
                      borderColor: formulation?.costs_complete ? 'success.main' : 'transparent'
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  {costsCompleteLoading ? 'Updating...' : 
                   formulation?.costs_complete ? 'Mark Incomplete' : 'Mark Complete'}
                </Button>
              )}
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}
      
      {/* Alerts Section */}
      {costs.ingredientCosts?.some(item => !item.has_pricing) && (
        <Alert 
          severity="warning" 
          sx={{ 
            mb: 3,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
            '& .MuiAlert-icon': {
              color: 'warning.main'
            }
          }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              startIcon={<ShoppingCart />}
              onClick={() => {
                if (formulation?.company_id) {
                  navigate(`/company/${formulation.company_id}/inventory/raw-materials`);
                }
              }}
              sx={{
                borderRadius: 2,
                fontWeight: 500
              }}
            >
              Manage Raw Materials
            </Button>
          }
        >
          Some raw materials are missing pricing information. Add supplier details to get accurate cost analysis.
        </Alert>
      )}

      {formulationIngredients.length === 0 && (
        <Alert 
          severity="info" 
          sx={{ 
            mb: 3,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`
          }}
        >
          No ingredients found. Add ingredients to your formulation to see cost analysis.
        </Alert>
      )}

      {/* Cost Parameters Section */}
      <Accordion 
        expanded={showCostsForm || editMode}
        elevation={0}
        sx={{
          mb: 2,
          borderRadius: 3,
          border: `1px solid ${theme.palette.grey[400]}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
          backdropFilter: 'blur(20px)',
          overflow: 'hidden',
          boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
          '&:before': {
            display: 'none'
          },
          '&.Mui-expanded': {
            margin: '0 0 16px 0'
          },
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMore sx={{ color: theme.palette.primary.main }} />}
          onClick={() => setShowCostsForm(!showCostsForm)}
          sx={{
            borderRadius: '12px 12px 0 0',
            background: 'white',
            backdropFilter: 'blur(10px)',
            border: 'none',
            minHeight: 72,
            '&.Mui-expanded': {
              minHeight: 72,
              borderRadius: '12px 12px 0 0'
            },
            '& .MuiAccordionSummary-content': {
              margin: '16px 0',
              alignItems: 'center',
              '&.Mui-expanded': {
                margin: '16px 0'
              }
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Box sx={{ 
              p: 1, 
              borderRadius: 2, 
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              color: theme.palette.primary.main,
              mr: 2
            }}>
              <AssessmentIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                Cost Parameters
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                Configure labor costs and overhead expenses for accurate pricing
              </Typography>
            </Box>
            {canEditCosts && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                {!costsInputted && !showCostsForm ? (
                  <Button 
                    variant="contained" 
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowCostsForm(true);
                    }}
                    startIcon={<Calculate />}
                    sx={{ 
                      borderRadius: 3,
                      px: 3,
                      py: 1.5,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                      fontWeight: 500,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                      '&:hover': {
                        background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Input Costs
                  </Button>
                ) : costsInputted && !editMode ? (
                  <Button 
                    variant="outlined" 
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditMode(true);
                    }}
                    startIcon={<Edit />}
                    size="small"
                    sx={{ 
                      borderRadius: 3,
                      px: 3,
                      py: 1.5,
                      background: 'white',
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      color: theme.palette.primary.main,
                      fontWeight: 500,
                      backdropFilter: 'blur(10px)',
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
                      '&:hover': {
                        background: 'white',
                        borderColor: theme.palette.primary.main,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.25)}`
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    Edit Costs
                  </Button>
                ) : (
                  <Button 
                    variant="contained" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSaveCosts();
                    }}
                    startIcon={<Save />}
                    sx={{ 
                      borderRadius: 3,
                      px: 3,
                      py: 1.5,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                      fontWeight: 500,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                      '&:hover': {
                        background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Save Costs
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3 }}>
          {costsInputted && !editMode && (
            <Box sx={{ 
              mb: 2, 
              p: 2, 
              borderRadius: 3, 
              background: 'white', 
              border: `1px solid ${theme.palette.grey[400]}`,
              boxShadow: `0 4px 16px rgba(0, 0, 0, 0.1)`,
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 24px rgba(0, 0, 0, 0.2)`
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
              <Typography variant="body1" sx={{ fontWeight: 500, color: 'success.main', mb: 1 }}>
                Production Cost Summary
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Production Cost: <strong>{formatCurrency(costs.laborCost + costs.overheadCost)}</strong> per batch
                {formulation?.target_batch_size && (
                  <span> ({convertUnits(formulation.target_batch_size, formulation.unit_of_measure || 'g', displayUnit).toFixed(2)} {displayUnit})</span>
                )}
              </Typography>
            </Box>
          )}
          
          {/* Labor Cost Section */}
          <Box                  sx={{ 
                   mb: 3, 
            p: 3, 
            borderRadius: 3, 
            border: `1px solid ${theme.palette.grey[400]}`, 
            background: 'white',
            boxShadow: `0 4px 16px rgba(0, 0, 0, 0.1)`,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 24px rgba(0, 0, 0, 0.2)`
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUpIcon sx={{ color: '#000000', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mr: 1 }}>
                  Labor Cost
                </Typography>
                <Tooltip title="Labor costs include manufacturing staff time, packaging labor, and quality control personnel needed to produce the formulation.">
                  <Info fontSize="small" color="action" />
                </Tooltip>
              </Box>
              <Chip 
                label={`Total: ${formatCurrency(costs.laborCost)}`}
                variant="outlined"
                sx={{ 
                  fontWeight: 500,
                  borderColor: '#000000',
                  color: '#000000',
                  background: 'white',
                  boxShadow: `0 2px 8px ${alpha(theme.palette.secondary.main, 0.15)}`,
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: `0 4px 16px ${alpha(theme.palette.secondary.main, 0.25)}`
                  },
                  transition: 'all 0.2s ease'
                }}
              />
            </Box>
            
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <Slider
                  value={laborPercentage}
                  onChange={(e, newValue) => setLaborPercentage(newValue)}
                  valueLabelDisplay="auto"
                  step={5}
                  marks
                  min={0}
                  max={100}
                  disabled={!canEditCosts || (!editMode && costsInputted)}
                  sx={{
                    '& .MuiSlider-thumb': {
                      backgroundColor: '#000000',
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
                      '&:hover': {
                        boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.4)}`
                      }
                    },
                    '& .MuiSlider-track': {
                      backgroundColor: '#000000'
                    },
                    '& .MuiSlider-rail': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.2)
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    value={laborPercentage}
                    onChange={(e) => {
                      const value = Math.min(Math.max(parseInt(e.target.value) || 0, 0), 100);
                      setLaborPercentage(value);
                    }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    variant="outlined"
                    size="small"
                    type="number"
                    sx={{ 
                      width: '120px',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        background: 'white',
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.08)}`,
                        '& fieldset': {
                          borderColor: '#e0e0e0',
                          borderWidth: 1
                        },
                        '&:hover fieldset': {
                          borderColor: '#000000'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#000000'
                        }
                      }
                    }}
                    disabled={!canEditCosts || (!editMode && costsInputted)}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    of raw material costs
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
          
          {/* Overhead Costs Section */}
          <Box sx={{ 
            p: 3, 
            borderRadius: 3, 
            border: `1px solid ${theme.palette.grey[400]}`, 
            background: 'white',
            boxShadow: `0 4px 16px rgba(0, 0, 0, 0.1)`,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 24px rgba(0, 0, 0, 0.2)`
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AccountBalanceIcon sx={{ color: '#000000', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mr: 1 }}>
                  Overhead Costs
                </Typography>
                <Tooltip title="Enter the overhead costs as percentages. Global overhead percentage applies to (material + labor) costs, then distributed among categories.">
                  <Info fontSize="small" color="action" />
                </Tooltip>
              </Box>
              <Chip 
                label={`Total: ${formatCurrency(costs.overheadCost)}`}
                variant="outlined"
                sx={{ 
                  fontWeight: 500,
                  borderColor: '#000000',
                  color: '#000000',
                  background: 'white',
                  boxShadow: `0 2px 8px ${alpha(theme.palette.secondary.main, 0.15)}`,
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: `0 4px 16px ${alpha(theme.palette.secondary.main, 0.25)}`
                  },
                  transition: 'all 0.2s ease'
                }}
              />
            </Box>
            
            {/* Global Overhead Percentage */}
            <Box sx={{ 
              mb: 2, 
              p: 2, 
              bgcolor: '#f0f9ff', 
              borderRadius: 3, 
              border: '1px solid #2196f3',
              boxShadow: `0 4px 16px ${alpha(theme.palette.info.main, 0.12)}`,
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 24px ${alpha(theme.palette.info.main, 0.2)}`
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 2, color: '#1976d2' }}>
                Total Overhead as % of (Material + Labor) Costs
              </Typography>
              <Box sx={{ px: 1, mb: 2 }}>
                <Slider
                  value={globalOverheadPercentage}
                  onChange={(e, newValue) => setGlobalOverheadPercentage(newValue)}
                  valueLabelDisplay="auto"
                  step={1}
                  min={10}
                  max={60}
                  marks={[
                    { value: 10, label: '10%' },
                    { value: 30, label: '30%' },
                    { value: 50, label: '50%' },
                    { value: 60, label: '60%' }
                  ]}
                  disabled={!canEditCosts || (!editMode && costsInputted)}
                  sx={{
                    '& .MuiSlider-thumb': {
                      backgroundColor: '#1976d2',
                      boxShadow: `0 2px 8px ${alpha(theme.palette.info.main, 0.3)}`,
                      '&:hover': {
                        boxShadow: `0 4px 16px ${alpha(theme.palette.info.main, 0.4)}`
                      }
                    },
                    '& .MuiSlider-track': {
                      backgroundColor: '#1976d2'
                    },
                    '& .MuiSlider-rail': {
                      backgroundColor: alpha(theme.palette.info.main, 0.2)
                    },
                    '& .MuiSlider-markLabel': {
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      '&[data-index="0"]': {
                        transform: 'translateX(0%)'
                      },
                      '&[data-index="3"]': {
                        transform: 'translateX(-100%)'
                      }
                    }
                  }}
                />
              </Box>
              <TextField
                size="small"
                label="Global Overhead Percentage"
                type="number"
                value={globalOverheadPercentage}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  if (!isNaN(val) && val >= 10 && val <= 60) {
                    setGlobalOverheadPercentage(val);
                  } else if (e.target.value === '') {
                    setGlobalOverheadPercentage(10);
                  }
                }}
                inputProps={{ min: 10, max: 60, step: 1 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>
                }}
                disabled={!canEditCosts || (!editMode && costsInputted)}
                sx={{ 
                  maxWidth: 200,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    background: 'white',
                    '& fieldset': {
                      borderColor: '#e0e0e0',
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: '#000000'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#000000'
                    }
                  }
                }}
              />
            </Box>
            
            {/* Enhanced Sticky Summary */}
            <Box sx={{ 
              p: 2,
              bgcolor: distributionTotal > 100 ? '#ffebee' : '#f0f9ff',
              borderRadius: 3,
              border: distributionTotal > 100 ? '1px solid #f44336' : '1px solid #2196f3',
              mb: 2,
              position: 'sticky',
              top: 10,
              zIndex: 10,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: distributionTotal > 100 ? '0 8px 24px rgba(244, 67, 54, 0.2)' : '0 8px 24px rgba(33, 150, 243, 0.2)'
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Global Overhead
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: '#1976d2' }}>
                    {globalOverheadPercentage}%
                  </Typography>
                </Grid>
                
                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Distribution Used
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    fontWeight: 500, 
                    color: distributionTotal > 100 ? '#f44336' : '#1976d2' 
                  }}>
                    {distributionTotal}%
                  </Typography>
                </Grid>
                
                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Overhead Cost
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: '#1976d2' }}>
                    {formatCurrency(costs.overheadCost)}
                  </Typography>
                </Grid>
                
                <Grid item xs={3}>
                  {distributionTotal > 100 ? (
                    <Button
                      size="small"
                      variant="contained"
                      color="error"
                      onClick={() => {
                        setUtilitiesPercentage(10);
                        setFacilityPercentage(25);
                        setEquipmentPercentage(15);
                        setAdminLaborPercentage(18);
                        setWarehousePercentage(7);
                        setQualityPercentage(10);
                        setSoftwarePercentage(8);
                        setInsurancePercentage(7);
                      }}
                      sx={{ fontSize: '0.75rem', py: 0.5, px: 1 }}
                    >
                      Fix Distribution
                    </Button>
                  ) : (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Status
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#2e7d32' }}>
                        ✓ Valid
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Box>
            
            {/* Simple List Layout */}
            <Stack spacing={1}>
              {[
                { label: 'Utilities', value: utilitiesPercentage, setter: setUtilitiesPercentage, color: '#2196f3' },
                { label: 'Facility', value: facilityPercentage, setter: setFacilityPercentage, color: '#4caf50' },
                { label: 'Equipment', value: equipmentPercentage, setter: setEquipmentPercentage, color: '#ff9800' },
                { label: 'Admin Labor', value: adminLaborPercentage, setter: setAdminLaborPercentage, color: '#9c27b0' },
                { label: 'Warehouse', value: warehousePercentage, setter: setWarehousePercentage, color: '#607d8b' },
                { label: 'Quality', value: qualityPercentage, setter: setQualityPercentage, color: '#e91e63' },
                { label: 'Software', value: softwarePercentage, setter: setSoftwarePercentage, color: '#795548' },
                { label: 'Insurance', value: insurancePercentage, setter: setInsurancePercentage, color: '#f44336' }
              ].map((item, index) => {
                 const actualPercentage = (item.value / 100) * globalOverheadPercentage;
                 
                 return (
                   <Box key={index} sx={{ 
                     p: 2,
                     border: '1px solid #e0e0e0',
                     borderRadius: 3,
                     bgcolor: '#fafafa',
                     boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.08)}`,
                     '&:hover': {
                       transform: 'translateY(-2px)',
                       boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
                       border: `1px solid ${item.color}`
                     },
                     transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                   }}>
                     <Grid container spacing={2} alignItems="center">
                       <Grid item xs={3}>
                         <Typography variant="body2" sx={{ 
                           fontWeight: 500, 
                           color: item.color
                         }}>
                           {item.label}
                         </Typography>
                         <Typography variant="caption" color="text.secondary">
                           = {actualPercentage.toFixed(1)}% total
                         </Typography>
                       </Grid>
                       
                       <Grid item xs={1}>
                         <Typography variant="h6" sx={{ 
                           fontWeight: 500, 
                           color: item.color,
                           textAlign: 'center'
                         }}>
                           {item.value}%
                         </Typography>
                       </Grid>
                       
                       <Grid item xs={8}>
                         <Slider
                           value={item.value}
                           onChange={(e, newValue) => {
                             // Ensure total doesn't exceed 100%
                             const otherTotal = distributionTotal - item.value;
                             const maxValue = Math.min(100 - otherTotal, newValue);
                             item.setter(Math.max(0, maxValue));
                           }}
                           step={1}
                           min={0}
                           max={100}
                           size="small"
                           disabled={!canEditCosts || (!editMode && costsInputted)}
                           sx={{
                             '& .MuiSlider-thumb': {
                               backgroundColor: item.color
                             },
                             '& .MuiSlider-track': {
                               backgroundColor: item.color
                             }
                           }}
                         />
                       </Grid>
                     </Grid>
                   </Box>
                 );
               })}
            </Stack>
          </Box>
          
                        {(!costsInputted || editMode) && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              {editMode && (
                <Button 
                  variant="outlined" 
                  onClick={() => setEditMode(false)}
                  sx={{ 
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                    color: theme.palette.text.secondary,
                    fontWeight: 500,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.04)
                    }
                  }}
                >
                  Cancel
                </Button>
              )}
              {canEditCosts && (
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={handleSaveCosts}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    color: 'white',
                    fontWeight: 500,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                  },
                  '&:disabled': {
                    background: theme.palette.grey[400],
                    color: theme.palette.grey[100]
                  }
                }}
              >
                Save Cost Parameters
              </Button>
              )}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      <Grid container spacing={3}>
        
        <Grid item xs={12}>
          <Accordion 
            defaultExpanded={false}
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: 3,
              border: `1px solid ${theme.palette.grey[400]}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              backdropFilter: 'blur(20px)',
              overflow: 'hidden',
              boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
              '&:before': {
                display: 'none'
              },
              '&.Mui-expanded': {
                margin: '0 0 24px 0'
              },
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            <AccordionSummary 
              expandIcon={<ExpandMore sx={{ color: theme.palette.primary.main }} />}
              sx={{
                borderRadius: '12px 12px 0 0',
                background: 'white',
                backdropFilter: 'blur(10px)',
                border: 'none',
                minHeight: 72,
                '&.Mui-expanded': {
                  minHeight: 72,
                  borderRadius: '12px 12px 0 0'
                },
                '& .MuiAccordionSummary-content': {
                  margin: '16px 0',
                  alignItems: 'center',
                  '&.Mui-expanded': {
                    margin: '16px 0'
                  }
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <Box sx={{ 
              p: 1, 
              borderRadius: 2, 
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              color: theme.palette.primary.main,
              mr: 2
            }}>
                  <ShoppingCart sx={{ fontSize: 20 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                Ingredient Cost Breakdown
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                Detailed cost analysis for each raw material in the formulation
              </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, color: 'success.main' }}>
                    {formatCurrency(costs.totalIngredientCost || 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Total Materials
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2 }}>
              <TableContainer 
                sx={{ 
                  borderRadius: 3,
                  border: `1px solid ${theme.palette.grey[400]}`,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
                  boxShadow: `0 4px 16px rgba(0, 0, 0, 0.1)`,
                  overflow: 'hidden',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 24px rgba(0, 0, 0, 0.2)`,
                    border: `1px solid ${theme.palette.grey[400]}`
                  }
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `1px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Ingredient
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `1px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Supplier
                      </TableCell>
                      <TableCell 
                        align="right" 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `1px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Amount ({displayUnit})
                      </TableCell>
                      <TableCell 
                        align="right" 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `1px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Cost per Unit
                      </TableCell>
                      <TableCell 
                        align="right" 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `1px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Total Cost
                      </TableCell>
                      <TableCell 
                        align="right" 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `1px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        % of Total
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {costs.ingredientCosts && costs.ingredientCosts.length > 0 ? (
                      costs.ingredientCosts.map((item, index) => (
                                              <TableRow 
                        key={`${item.ingredient_id}-${index}`}
                        hover
                        sx={{ 
                          cursor: 'default',
                          transition: 'all 0.2s ease',
                          '&:hover': { 
                            bgcolor: 'grey.50',
                            transform: 'translateX(4px)',
                            transition: 'all 0.2s ease',
                            boxShadow: 'inset 4px 0 0 black'
                          },
                          '& .MuiTableCell-root': {
                            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                            py: 2.5,
                            px: 3
                          }
                        }}
                      >
                          <TableCell sx={{ py: 2 }}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                {item.ingredient_name}
                              </Typography>
                              {item.phases_used && item.phases_used.length > 1 && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                  Used in {item.phases_used.length} phases ({item.percentage.toFixed(2)}% total)
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            {item.has_pricing ? (
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {item.supplier_name}
                              </Typography>
                            ) : (
                              <Chip
                                label="No supplier pricing"
                                size="small"
                                sx={{
                                  backgroundColor: alpha(theme.palette.warning.main, 0.15),
                                  color: 'warning.dark',
                                  fontWeight: 500,
                                  border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                                  fontSize: '0.75rem'
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                              {item.amount.toFixed(2)}
                            </Typography>
                            {item.show_raw_material_conversion && (
                              <Typography variant="caption" color="primary.main" sx={{ display: 'block', mt: 0.5, fontWeight: 500 }}>
                                ≈{item.raw_material_amount.toFixed(3)} {item.raw_material_unit} raw
                              </Typography>
                            )}
                            {item.density_display && !item.show_raw_material_conversion && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                (≈{item.density_display.amount.toFixed(2)} {item.density_display.unit} actual)
                              </Typography>
                            )}
                            {item.material_density && item.material_density !== 1.0 && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
                                density: {item.material_density.toFixed(3)} g/ml
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 2 }}>
                            {item.has_pricing ? (
                              <Tooltip title={`Raw material unit: ${item.unit_info}`}>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                                  {formatCurrency(item.cost_per_unit)}/{item.raw_material_unit_measure}
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" color="text.disabled">—</Typography>
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 2 }}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 500, 
                                color: item.has_pricing ? 'success.main' : 'text.disabled'
                              }}
                            >
                              {item.has_pricing ? formatCurrency(item.total_cost) : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ py: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'info.main' }}>
                              {item.has_pricing && costs.totalIngredientCost > 0
                                ? `${((item.total_cost / costs.totalIngredientCost) * 100).toFixed(1)}%`
                                : '—'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                          <Box sx={{ textAlign: 'center' }}>
                            <ShoppingCart sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                            <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                              No cost data available
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Make sure your formulation has ingredients with suppliers
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                    {costs.ingredientCosts && costs.ingredientCosts.length > 0 && (
                                          <TableRow sx={{ 
                      borderTop: `1px solid ${theme.palette.primary.main}`,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
                    }}>
                      <TableCell>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>Total:</Typography>
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell align="right" sx={{ py: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'success.main' }}>
                          {formatCurrency(costs.totalIngredientCost || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'success.main' }}>
                          100%
                        </Typography>
                      </TableCell>
                    </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Grid item xs={12}>
          <Accordion 
            defaultExpanded={false}
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: 3,
              border: `1px solid ${theme.palette.grey[400]}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              backdropFilter: 'blur(20px)',
              overflow: 'hidden',
              boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
              '&:before': {
                display: 'none'
              },
              '&.Mui-expanded': {
                margin: '0 0 24px 0'
              },
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            <AccordionSummary 
              expandIcon={<ExpandMore sx={{ color: theme.palette.primary.main }} />}
              sx={{
                borderRadius: '12px 12px 0 0',
                background: 'white',
                backdropFilter: 'blur(10px)',
                border: 'none',
                minHeight: 72,
                '&.Mui-expanded': {
                  minHeight: 72,
                  borderRadius: '12px 12px 0 0'
                },
                '& .MuiAccordionSummary-content': {
                  margin: '16px 0',
                  alignItems: 'center',
                  '&.Mui-expanded': {
                    margin: '16px 0'
                  }
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: '#f5f5f5',
                  border: '1px solid #e0e0e0',
                  color: '#000000',
                  mr: 3
                }}>
                  <AssessmentIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500, color: '#000000', mb: 0.5 }}>
                    Cost Summary
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    For {convertUnits(formulation.target_batch_size || 0, formulation.unit_of_measure || 'g', displayUnit).toFixed(2)} {displayUnit} batch
                    {formulation?.unit_of_measure !== displayUnit && (
                      <Tooltip title={`Original batch size: ${formulation.target_batch_size} ${formulation.unit_of_measure}`}>
                        <Info fontSize="small" color="action" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                      </Tooltip>
                    )}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, color: 'info.main' }}>
                    {formatCurrency(costs.costPerUnit || 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Per {displayUnit}
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 2, 
                      border: `1px solid ${theme.palette.grey[400]}`, 
                      borderRadius: 3,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
                      backdropFilter: 'blur(10px)',
                      height: '100%',
                      boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.08)}`,
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                      Production Costs
                    </Typography>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000' }}>Raw Materials:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                          {formatCurrency(costs.totalIngredientCost || 0)}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000' }}>Production Costs:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000' }}>
                          {formatCurrency((costs.laborCost || 0) + (costs.overheadCost || 0))}
                        </Typography>
                      </Box>
                      
                      <Divider sx={{ my: 1 }} />
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: '#000000' }}>Total Production Cost:</Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'success.main', fontSize: '1.1rem' }}>
                          {formatCurrency(costs.totalCost || 0)}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000' }}>
                          Cost per {displayUnit}:
                          {formulation?.unit_of_measure !== displayUnit && (
                            <Tooltip title={`Converted from ${formulation?.unit_of_measure || 'g'} to ${displayUnit}`}>
                              <Info fontSize="small" color="action" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                            </Tooltip>
                          )}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'info.main' }}>
                          {formatCurrency(costs.costPerUnit || 0)}/{displayUnit}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 2, 
                      border: `1px solid ${theme.palette.grey[400]}`, 
                      borderRadius: 3,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
                      backdropFilter: 'blur(10px)',
                      height: '100%',
                      boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.08)}`,
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                      Production Costs Breakdown
                    </Typography>
                    
                    <Stack spacing={1.5}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000' }}>Labor ({laborPercentage}%):</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatCurrency(costs.laborCost || 0)}
                        </Typography>
                      </Box>
                      
                      {[
                        { label: 'Utilities', percentage: utilitiesPercentage },
                        { label: 'Facility', percentage: facilityPercentage },
                        { label: 'Equipment', percentage: equipmentPercentage },
                        { label: 'Admin Labor', percentage: adminLaborPercentage },
                        { label: 'Warehouse', percentage: warehousePercentage },
                        { label: 'Quality', percentage: qualityPercentage },
                        { label: 'Software', percentage: softwarePercentage },
                        { label: 'Insurance', percentage: insurancePercentage }
                      ].map((item, index) => {
                        const actualCost = costs.overheadCost * (item.percentage / 100);
                        return (
                          <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000' }}>
                              {item.label} ({item.percentage}%):
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {formatCurrency(actualCost)}
                            </Typography>
                          </Box>
                        );
                      })}
                      
                      <Divider sx={{ my: 1 }} />
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000' }}>Total Production Cost:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main' }}>
                          {formatCurrency((costs.laborCost || 0) + (costs.overheadCost || 0))}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Grid item xs={12} sx={{ mt: 4 }}>
          <Accordion 
            defaultExpanded={false}
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: 3,
              border: `1px solid ${theme.palette.grey[400]}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              backdropFilter: 'blur(20px)',
              overflow: 'hidden',
              boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
              '&:before': {
                display: 'none'
              },
              '&.Mui-expanded': {
                margin: '0 0 24px 0'
              },
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            <AccordionSummary 
              expandIcon={<ExpandMore sx={{ color: theme.palette.primary.main }} />}
              sx={{
                borderRadius: '12px 12px 0 0',
                background: 'white',
                backdropFilter: 'blur(10px)',
                border: 'none',
                minHeight: 72,
                '&.Mui-expanded': {
                  minHeight: 72,
                  borderRadius: '12px 12px 0 0'
                },
                '& .MuiAccordionSummary-content': {
                  margin: '16px 0',
                  alignItems: 'center',
                  '&.Mui-expanded': {
                    margin: '16px 0'
                  }
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: '#f5f5f5',
                  border: '1px solid #e0e0e0',
                  color: '#000000',
                  mr: 3
                }}>
                  <AttachMoneyIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500, color: '#000000', mb: 0.5 }}>
                    Total Production Cost
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Complete cost analysis with pricing calculator and final totals
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, color: 'success.main' }}>
                    {formatCurrency(costs.totalCost || 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Total Batch Cost
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2 }}>
            
              <Divider sx={{ my: 3, borderColor: alpha(theme.palette.divider, 0.3) }} />
             
             {/* Product Pricing Calculator */}
                            <Paper elevation={0} sx={{ 
               p: 3, 
               borderRadius: 3,
               border: `1px solid ${theme.palette.grey[400]}`,
               background: 'white',
               mb: 3,
               boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
               '&:hover': {
                 transform: 'translateY(-2px)',
                 boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.25)}`
               },
               transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
             }}>
               <Box sx={{ 
                 display: 'flex', 
                 alignItems: 'center',
                 mb: 3
               }}>
                 <Box sx={{ 
                   p: 2, 
                   borderRadius: 3, 
                   background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.primary.main, 0.08)})`,
                   border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                   mr: 3
                 }}>
                   <Calculate sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                 </Box>
                 <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                   Product Pricing Calculator
                 </Typography>
               </Box>
               
               <Alert 
                 severity="info" 
                 sx={{ 
                   mb: 3,
                   borderRadius: 3,
                   border: `1px solid ${alpha(theme.palette.info.main, 0.4)}`,
                   background: 'white',
                   boxShadow: `0 4px 16px ${alpha(theme.palette.info.main, 0.12)}`,
                   '&:hover': {
                     transform: 'translateY(-2px)',
                     boxShadow: `0 8px 24px ${alpha(theme.palette.info.main, 0.2)}`
                   },
                   transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                   '& .MuiAlert-icon': {
                     color: 'info.main'
                   },
                   '& .MuiAlert-message': {
                     fontWeight: 500
                   }
                 }}
               >
                 Estimate final product pricing. Note: Does not include packaging costs (bottles, caps, labels).
               </Alert>
               
               <Grid container spacing={2}>
                 <Grid item xs={12} md={4}>
                   <Paper elevation={0} sx={{ 
                     p: 4, 
                     borderRadius: 3,
                     border: `1px solid ${alpha(theme.palette.info.main, 0.4)}`,
                     background: alpha(theme.palette.info.main, 0.04),
                     height: '100%',
                     boxShadow: `0 8px 24px ${alpha(theme.palette.info.main, 0.15)}`,
                     '&:hover': {
                       transform: 'translateY(-6px)',
                       boxShadow: `0 16px 40px ${alpha(theme.palette.info.main, 0.25)}`,
                       border: `1px solid ${alpha(theme.palette.info.main, 0.6)}`
                     },
                     transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                   }}>
                     <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                       <Box sx={{ 
                         p: 2, 
                         borderRadius: 3, 
                         background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.15)}, ${alpha(theme.palette.info.main, 0.08)})`,
                         border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                         mr: 3
                       }}>
                         <ShoppingCart sx={{ color: 'info.main', fontSize: 24 }} />
                       </Box>
                       <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                         Package Size
                       </Typography>
                     </Box>
                     
                     <Grid container spacing={3} alignItems="center" sx={{ mb: 4 }}>
                       <Grid item xs={6}>
                         <TextField
                           label="Size"
                           type="number"
                           size="small"
                           value={packageSize}
                           onChange={(e) => {
                             const value = parseFloat(e.target.value);
                             if (isNaN(value) || value < 0) {
                               setPackageSize(0);
                             } else {
                               setPackageSize(value);
                             }
                           }}
                           inputProps={{ min: 0, step: 0.1 }}
                           fullWidth
                           sx={{
                             '& .MuiOutlinedInput-root': {
                               borderRadius: 3,
                               background: 'white',
                               '& fieldset': {
                                 borderColor: alpha(theme.palette.info.main, 0.3),
                                 borderWidth: 1
                               },
                               '&:hover fieldset': {
                                 borderColor: 'info.main'
                               },
                               '&.Mui-focused fieldset': {
                                 borderColor: 'info.main',
                                 borderWidth: 1
                               }
                             }
                           }}
                         />
                       </Grid>
                       <Grid item xs={6}>
                         <FormControl size="small" fullWidth>
                           <InputLabel>Unit</InputLabel>
                           <Select
                             value={packageUnit}
                             onChange={(e) => setPackageUnit(e.target.value)}
                             label="Unit"
                             sx={{ 
                               borderRadius: 3,
                               background: 'white',
                               '& .MuiOutlinedInput-notchedOutline': {
                                 borderColor: alpha(theme.palette.info.main, 0.3),
                                 borderWidth: 1
                               },
                               '&:hover .MuiOutlinedInput-notchedOutline': {
                                 borderColor: 'info.main'
                               },
                               '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                 borderColor: 'info.main',
                                 borderWidth: 1
                               }
                             }}
                           >
                             <MenuItem value="oz">oz (weight)</MenuItem>
                             <MenuItem value="ml">ml</MenuItem>
                             <MenuItem value="g">g</MenuItem>
                             <MenuItem value="lb">lb</MenuItem>
                           </Select>
                         </FormControl>
                       </Grid>
                     </Grid>
                     
                     <Box sx={{ 
                       p: 4, 
                       borderRadius: 3, 
                       background: 'white',
                       border: `1px solid ${alpha(theme.palette.info.main, 0.4)}`,
                       textAlign: 'center',
                       boxShadow: `0 4px 16px ${alpha(theme.palette.info.main, 0.12)}`,
                       '&:hover': {
                         transform: 'scale(1.02)',
                         boxShadow: `0 8px 24px ${alpha(theme.palette.info.main, 0.2)}`
                       },
                       transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                     }}>
                       <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontWeight: 500 }}>
                         Raw Material Cost
                       </Typography>
                       <Typography variant="h3" sx={{ fontWeight: 500, color: 'info.main' }}>
                         ${packageCostValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                       </Typography>
                     </Box>
                   </Paper>
                 </Grid>
                 
                 <Grid item xs={12} md={4}>
                   <Paper elevation={0} sx={{ 
                     p: 4, 
                     borderRadius: 3,
                     border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
                     background: alpha(theme.palette.warning.main, 0.04),
                     height: '100%',
                     boxShadow: `0 8px 24px ${alpha(theme.palette.warning.main, 0.15)}`,
                     '&:hover': {
                       transform: 'translateY(-6px)',
                       boxShadow: `0 16px 40px ${alpha(theme.palette.warning.main, 0.25)}`,
                       border: `1px solid ${alpha(theme.palette.warning.main, 0.6)}`
                     },
                     transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                   }}>
                     <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                       <Box sx={{ 
                         p: 2, 
                         borderRadius: 3, 
                         background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.15)}, ${alpha(theme.palette.warning.main, 0.08)})`,
                         border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                         mr: 3
                       }}>
                         <TrendingUpIcon sx={{ color: 'warning.main', fontSize: 24 }} />
                       </Box>
                       <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                         Markup Settings
                       </Typography>
                     </Box>
                     
                     <Grid container spacing={3} sx={{ mb: 4 }}>
                       <Grid item xs={12}>
                         <TextField
                           label="Markup Percentage"
                           type="number"
                           size="small"
                           value={markupPercentage}
                           onChange={(e) => {
                             const value = parseFloat(e.target.value);
                             if (isNaN(value) || value < 0) {
                               setMarkupPercentage(0);
                             } else {
                               setMarkupPercentage(value);
                             }
                           }}
                           inputProps={{ min: 0, step: 10 }}
                           InputProps={{
                             endAdornment: <InputAdornment position="end">%</InputAdornment>,
                           }}
                           helperText={`Multiplier: ${((markupPercentage + 100) / 100).toFixed(1)}x cost`}
                           fullWidth
                           sx={{
                             '& .MuiOutlinedInput-root': {
                               borderRadius: 3,
                               background: 'white',
                               '& fieldset': {
                                 borderColor: alpha(theme.palette.warning.main, 0.3),
                                 borderWidth: 1
                               },
                               '&:hover fieldset': {
                                 borderColor: 'warning.main'
                               },
                               '&.Mui-focused fieldset': {
                                 borderColor: 'warning.main',
                                 borderWidth: 1
                               }
                             }
                           }}
                         />
                       </Grid>
                       <Grid item xs={12}>
                         <TextField
                           label="Packaging Cost"
                           type="number"
                           size="small"
                           value={packagingCost}
                           onChange={(e) => {
                             const value = parseFloat(e.target.value);
                             if (isNaN(value) || value < 0) {
                               setPackagingCost(0);
                             } else {
                               setPackagingCost(value);
                             }
                           }}
                           inputProps={{ min: 0, step: 0.1 }}
                           InputProps={{
                             startAdornment: <InputAdornment position="start">$</InputAdornment>,
                           }}
                           fullWidth
                           sx={{
                             '& .MuiOutlinedInput-root': {
                               borderRadius: 3,
                               background: 'white',
                               '& fieldset': {
                                 borderColor: alpha(theme.palette.warning.main, 0.3),
                                 borderWidth: 1
                               },
                               '&:hover fieldset': {
                                 borderColor: 'warning.main'
                               },
                               '&.Mui-focused fieldset': {
                                 borderColor: 'warning.main',
                                 borderWidth: 1
                               }
                             }
                           }}
                         />
                       </Grid>
                     </Grid>
                     
                     <Box sx={{ 
                       p: 4, 
                       borderRadius: 3, 
                       background: 'white',
                       border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
                       textAlign: 'center',
                       boxShadow: `0 4px 16px ${alpha(theme.palette.warning.main, 0.12)}`,
                       '&:hover': {
                         transform: 'scale(1.02)',
                         boxShadow: `0 8px 24px ${alpha(theme.palette.warning.main, 0.2)}`
                       },
                       transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                     }}>
                       <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontWeight: 500 }}>
                         Total Cost Base
                       </Typography>
                       <Typography variant="h3" sx={{ fontWeight: 500, color: 'warning.main' }}>
                         ${(packageCostValue + packagingCost).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                       </Typography>
                     </Box>
                   </Paper>
                 </Grid>
                 
                 <Grid item xs={12} md={4}>
                   <Paper elevation={0} sx={{ 
                     p: 4, 
                     borderRadius: 3,
                     background: alpha(theme.palette.success.main, 0.04),
                     border: `1px solid ${alpha(theme.palette.success.main, 0.4)}`,
                     height: '100%',
                     position: 'relative',
                     overflow: 'hidden',
                     boxShadow: `0 8px 24px ${alpha(theme.palette.success.main, 0.15)}`,
                     '&:hover': {
                       transform: 'translateY(-6px)',
                       boxShadow: `0 16px 40px ${alpha(theme.palette.success.main, 0.25)}`,
                       border: `1px solid ${alpha(theme.palette.success.main, 0.6)}`
                     },
                     transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                   }}>
                     <Box sx={{ 
                       position: 'absolute',
                       top: -30,
                       right: -30,
                       width: 120,
                       height: 120,
                       borderRadius: '50%',
                       background: alpha(theme.palette.success.main, 0.1)
                     }} />
                     
                     <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, position: 'relative', zIndex: 1 }}>
                       <Box sx={{ 
                         p: 2, 
                         borderRadius: 3, 
                         background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.15)}, ${alpha(theme.palette.success.main, 0.08)})`,
                         border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                         mr: 3
                       }}>
                         <AttachMoneyIcon sx={{ color: 'success.main', fontSize: 24 }} />
                       </Box>
                       <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                         Retail Price
                       </Typography>
                     </Box>
                     
                     <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
                       <Typography variant="body2" sx={{ mb: 3, fontWeight: 500, color: theme.palette.text.primary }}>
                         Suggested Selling Price
                       </Typography>
                       <Typography variant="h2" sx={{ 
                         fontWeight: 500, 
                         mb: 2,
                         color: 'success.main',
                         textShadow: `0 2px 8px ${alpha(theme.palette.success.main, 0.3)}`
                       }}>
                         ${retailPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                       </Typography>
                       <Typography variant="body2" sx={{ mb: 3, color: theme.palette.text.secondary, fontWeight: 500 }}>
                         {markupPercentage}% markup ({((markupPercentage + 100) / 100).toFixed(1)}x cost)
                       </Typography>
                       <Box sx={{ 
                         mt: 3, 
                         p: 2, 
                         borderRadius: 3, 
                         background: 'white',
                         border: `1px solid ${alpha(theme.palette.success.main, 0.4)}`,
                         boxShadow: `0 4px 16px ${alpha(theme.palette.success.main, 0.12)}`,
                         '&:hover': {
                           transform: 'scale(1.02)',
                           boxShadow: `0 8px 24px ${alpha(theme.palette.success.main, 0.2)}`
                         },
                         transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                       }}>
                         <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '1rem', color: theme.palette.text.primary }}>
                           Profit Margin: {profitMarginPercentage.toFixed(1)}%
                         </Typography>
                       </Box>
                     </Box>
                   </Paper>
                 </Grid>
               </Grid>
               
               {/* Profit Analysis Summary */}
               <Box sx={{ 
                 mt: 4, 
                 p: 4, 
                 borderRadius: 3,
                 background: 'white',
                 border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                 boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                 '&:hover': {
                   transform: 'translateY(-2px)',
                   boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.25)}`
                 },
                 transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
               }}>
                 <Typography variant="h5" sx={{ fontWeight: 500, mb: 4, textAlign: 'center', color: theme.palette.text.primary }}>
                   Profitability Analysis
                 </Typography>
                 
                 <Grid container spacing={4} alignItems="center">
                   <Grid item xs={12} md={3}>
                     <Box sx={{ 
                       textAlign: 'center',
                       p: 2,
                       borderRadius: 3,
                       background: alpha(theme.palette.grey[400], 0.04),
                       border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`,
                       boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.08)}`,
                       '&:hover': {
                         transform: 'translateY(-2px)',
                         boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`
                       },
                       transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                     }}>
                       <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
                         TOTAL COST
                       </Typography>
                       <Typography variant="h4" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                         ${totalCostBase.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                       </Typography>
                     </Box>
                   </Grid>
                   
                   <Grid item xs={12} md={3}>
                     <Box sx={{ 
                       textAlign: 'center',
                       p: 2,
                       borderRadius: 3,
                       background: alpha(theme.palette.success.main, 0.04),
                       border: `1px solid ${alpha(theme.palette.success.main, 0.4)}`,
                       boxShadow: `0 4px 16px ${alpha(theme.palette.success.main, 0.12)}`,
                       '&:hover': {
                         transform: 'translateY(-2px)',
                         boxShadow: `0 8px 24px ${alpha(theme.palette.success.main, 0.2)}`
                       },
                       transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                     }}>
                       <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
                         SELLING PRICE
                       </Typography>
                       <Typography variant="h4" sx={{ fontWeight: 500, color: 'success.main' }}>
                         ${retailPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                       </Typography>
                     </Box>
                   </Grid>
                   
                   <Grid item xs={12} md={3}>
                     <Box sx={{ 
                       textAlign: 'center',
                       p: 2,
                       borderRadius: 3,
                       background: alpha(theme.palette.warning.main, 0.04),
                       border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
                       boxShadow: `0 4px 16px ${alpha(theme.palette.warning.main, 0.12)}`,
                       '&:hover': {
                         transform: 'translateY(-2px)',
                         boxShadow: `0 8px 24px ${alpha(theme.palette.warning.main, 0.2)}`
                       },
                       transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                     }}>
                       <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
                         MARKUP %
                       </Typography>
                       <Typography variant="h4" sx={{ fontWeight: 500, color: 'warning.main' }}>
                         {markupPercentage.toFixed(0)}%
                       </Typography>
                     </Box>
                   </Grid>
                   
                   <Grid item xs={12} md={3}>
                     <Box sx={{ 
                       textAlign: 'center',
                       p: 2,
                       borderRadius: 3,
                       background: alpha(theme.palette.success.main, 0.04),
                       border: `1px solid ${alpha(theme.palette.success.main, 0.4)}`,
                       boxShadow: `0 4px 16px ${alpha(theme.palette.success.main, 0.12)}`,
                       '&:hover': {
                         transform: 'translateY(-2px)',
                         boxShadow: `0 8px 24px ${alpha(theme.palette.success.main, 0.2)}`
                       },
                       transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                     }}>
                       <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
                         PROFIT MARGIN %
                       </Typography>
                       <Typography variant="h4" sx={{ fontWeight: 500, color: 'success.main' }}>
                         {profitMarginPercentage.toFixed(1)}%
                       </Typography>
                     </Box>
                   </Grid>
                 </Grid>
               </Box>
             </Paper>
             
             <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Box sx={{ 
                  p: 2, 
                  textAlign: 'center',
                  borderRadius: 3,
                  background: 'white',
                  border: `1px solid ${theme.palette.grey[400]}`,
                  boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.08)}`,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}>
                  <Typography variant="h5" sx={{ 
                    fontWeight: 500, 
                    color: theme.palette.text.primary,
                    mb: 0.5
                  }}>
                    {formatCurrency(costs.totalIngredientCost || 0)}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: theme.palette.text.primary,
                    fontWeight: 500,
                    fontSize: '0.8rem'
                  }}>
                    Raw Materials
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box sx={{ 
                  p: 2, 
                  textAlign: 'center',
                  borderRadius: 3,
                  background: 'white',
                  border: `1px solid ${theme.palette.grey[400]}`,
                  boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.08)}`,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}>
                  <Typography variant="h5" sx={{ 
                    fontWeight: 500, 
                    color: theme.palette.text.primary,
                    mb: 0.5
                  }}>
                    {formatCurrency(costs.laborCost || 0)}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: theme.palette.text.primary,
                    fontWeight: 500,
                    fontSize: '0.8rem'
                  }}>
                    Labor ({laborPercentage}%)
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box sx={{ 
                  p: 2, 
                  textAlign: 'center',
                  borderRadius: 3,
                  background: 'white',
                  border: `1px solid ${theme.palette.grey[400]}`,
                  boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.08)}`,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}>
                  <Typography variant="h5" sx={{ 
                    fontWeight: 500, 
                    color: theme.palette.text.primary,
                    mb: 0.5
                  }}>
                    {formatCurrency(costs.overheadCost)}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: theme.palette.text.primary,
                    fontWeight: 500,
                    fontSize: '0.8rem'
                  }}>
                    Overhead
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>
      </Box>
    </Box>
  );
};

export default CostsTab; 